// 预渲染内嵌状态读取（水合首帧一致性 / Requirement 11.13）
//
// ─── 契约说明（本文件是「读」的一端，Task 14.5 的 scripts/prerender/serialize.ts 是「写」的一端） ───
//
// 预渲染阶段（尚未实现，Task 14.5）会在输出的 HTML 中插入一段：
//
//   <script id="__GEO_STATE__" type="application/json">
//     {"v":1,"cacheKeys":{...},"direct":{...}}
//   </script>
//
// 本文件在模块加载时一次性解析这段 JSON，之后只做只读查表，不再重新解析、不再变更
// 已缓存的解析结果——这正是「纯函数、可重复读取（兼容 StrictMode 双渲染）」的含义：
// React StrictMode 在开发环境会重复调用组件渲染函数与 `useState` 初始化函数，若
// `readPreload()` 内部有副作用或每次重新解析 DOM，重复调用可能得到不一致的结果；
// 这里通过模块级变量缓存解析结果，保证多次调用 `readPreload(key)`（不区分是否
// StrictMode 双渲染）对同一个 key 永远返回同一个值。
//
// 内嵌 JSON 的形状（Task 14.5 的序列化端必须产出与此完全一致的结构）：
//
//   {
//     v: number;                    // 版本号，当前固定为 PRELOAD_VERSION
//     cacheKeys: Record<string, unknown>;
//     direct: Record<string, unknown>;
//   }
//
// - `v` 版本号不认识（缺失、非数字、或数值与本文件的 PRELOAD_VERSION 不一致）时，
//   整段内嵌状态被视为不存在（不使用 cacheKeys，也不使用 direct），退化为纯客户端
//   渲染——这是任务要求的显式降级路径，避免新旧版本的序列化格式不兼容时读出脏数据。
//
// - `cacheKeys` 桶：键沿用现有 `cachedRequest(key, ...)` 调用点已经在用的短字符串
//   （例如 'home'、'solutions/tree'、'seo'、'products/list?pageNum=1&pageSize=9999' 等，
//   参见 src/api/homeApi.ts、solutionApi.ts、productApi.ts、caseApi.ts 里 cachedRequest
//   第一个参数的实际取值）。选择「沿用既有短键」而不是这里描述的
//   `{method} {url}?{params}` 格式，是因为前者可以做到零改动接入：现有页面组件
//   继续调用 `getHomeData()` / `getSolutionTree()` 等函数，函数内部的
//   `cachedRequest('home', ...)` 会在 store 里发现本文件已经在模块加载时 primeCache
//   过的同名 key，直接命中缓存，完全不需要改动 homeApi.ts / solutionApi.ts 等文件
//   里已有的 cachedRequest 调用。`{method} {url}?{params}` 格式专门保留给下面的
//   `direct` 桶，用于那些原本没有任何缓存键约定的「直连」调用。
//   本文件不通过 `readPreload()` 暴露 `cacheKeys`——它们在模块加载时被直接灌入
//   src/api/cache.ts 的内部 store（通过 `primeCache`），此后完全由 cache.ts 自己的
//   `cachedRequest()` 逻辑接管。
//
// - `direct` 桶：键格式为 `{method} {url}?{序列化后的 params}`，其中 params 部分
//   复用 src/api/cache.ts 的 `paramsKey()`（按参数名字典序序列化为 `k=v&k=v`），
//   保证预渲染时（Task 14.5）与运行时（本文件的 `buildPreloadKey`）对同一个逻辑请求
//   算出完全相同的 key 字符串。这个桶通过 `readPreload<T>(key)` 对外暴露，供：
//     (a) 本任务的 `withPreload` 适配器包装的直连调用（getSceneDetail、
//         getProductById 等，它们原本不走 cachedRequest，没有既有缓存键约定）；
//     (b) 未来 Task 11.5 给约 13 个页面组件接入的首帧初值
//         `useState(() => readPreload<T>(key) ?? null)`（该任务会另行约定自己的
//         key 命名，`readPreload` 本身只是通用的只读查表，不关心 key 的命名来源）。
//
// 综上：`cacheKeys` 与 `direct` 是两套独立的 key 命名空间，服务两类不同的调用方式，
// 互不冲突，也不要求彼此的 key 格式统一。

import { primeCache, paramsKey } from './cache';

/** 内嵌状态脚本标签的 id，与 Task 14.5 的序列化端约定一致。 */
const SCRIPT_ID = '__GEO_STATE__';

/**
 * 本文件认识的内嵌状态版本号。序列化端（Task 14.5）若升级了 JSON 形状，
 * 必须同步提升这个数字；读取端遇到不认识的版本号会整体忽略内嵌状态，
 * 不会尝试兼容解析，避免用旧版本的字段假设去读新格式（或反之）导致的脏数据。
 */
export const PRELOAD_VERSION = 1;

interface GeoPreloadState {
  v: number;
  cacheKeys: Record<string, unknown>;
  direct: Record<string, unknown>;
}

/** 解析 `<script id="__GEO_STATE__">`，任何异常情况都返回 null（纯客户端渲染降级），不抛出。 */
function parseEmbeddedState(): GeoPreloadState | null {
  if (typeof document === 'undefined') return null;

  const el = document.getElementById(SCRIPT_ID);
  if (!el || !el.textContent) return null;

  let raw: unknown;
  try {
    raw = JSON.parse(el.textContent);
  } catch {
    return null;
  }

  if (typeof raw !== 'object' || raw === null) return null;
  const obj = raw as Record<string, unknown>;

  if (typeof obj.v !== 'number' || obj.v !== PRELOAD_VERSION) {
    // 版本号不认识：整段内嵌状态视为不存在。
    return null;
  }

  const cacheKeys = obj.cacheKeys;
  const direct = obj.direct;

  return {
    v: obj.v,
    cacheKeys: cacheKeys && typeof cacheKeys === 'object' ? (cacheKeys as Record<string, unknown>) : {},
    direct: direct && typeof direct === 'object' ? (direct as Record<string, unknown>) : {},
  };
}

// 模块初始化时一次性解析（顶层副作用，不放在函数里），并缓存到模块级变量。
// 之后的所有 readPreload() 调用都只读这个缓存，不会重新访问 DOM / 重新 JSON.parse。
const embeddedState: GeoPreloadState | null = parseEmbeddedState();

// 启动时把 cacheKeys 列出的条目灌入 src/api/cache.ts 的 primeCache，
// 消除首屏重复请求（既有的 cachedRequest('home', ...) 等调用会直接命中）。
if (embeddedState) {
  for (const [key, value] of Object.entries(embeddedState.cacheKeys)) {
    primeCache(key, value);
  }
}

/**
 * 纯函数查表：从内嵌状态的 `direct` 桶里读取 key 对应的值。
 *
 * - 没有内嵌状态（脚本标签缺失 / JSON 解析失败 / 版本号不认识）时始终返回 undefined。
 * - 找不到该 key 时返回 undefined。
 * - 不产生任何副作用，可重复调用（StrictMode 双渲染安全）。
 */
export function readPreload<T>(key: string): T | undefined {
  if (!embeddedState) return undefined;
  if (!Object.prototype.hasOwnProperty.call(embeddedState.direct, key)) return undefined;
  return embeddedState.direct[key] as T;
}

/**
 * 构造 `direct` 桶使用的 key：`{method} {url}?{序列化后的 params}`。
 *
 * params 的序列化直接复用 src/api/cache.ts 的 `paramsKey()`，保证预渲染时
 * （Task 14.5 的序列化端）与运行时（这里）对同一个逻辑请求算出完全相同的字符串。
 */
export function buildPreloadKey(method: string, url: string, params?: Record<string, unknown>): string {
  return `${method} ${url}?${paramsKey(params)}`;
}

// 记录已经被消费过的 direct key：同一个 key 在同一次页面生命周期内只允许命中一次
// 内嵌预载值，第二次命中同一个 key 视为「后续真实交互」，必须转发给真实请求，
// 不能继续返回预渲染时刻的旧数据。
const consumedDirectKeys = new Set<string>();

/**
 * 为「不走 cachedRequest 的直连调用」（如 getSceneDetail、getProductById）包一层
 * 预载适配器：
 *
 * - 首屏（内嵌状态中存在对应 key 且尚未被消费过）：直接返回内嵌值，标记该 key 为
 *   已消费，不调用真实 fetcher。
 * - 其余情况（无内嵌状态 / key 不存在 / 该 key 已被消费过一次）：调用真实 fetcher。
 *
 * @param method       HTTP 方法（约定使用大写，如 'GET'），用于拼 key，不影响实际请求
 *                      （实际请求的方法由 fetcher 内部决定）。
 * @param urlTemplate   根据调用参数算出请求地址（用于拼 key，需与 fetcher 内部真实
 *                      请求的地址一致）。
 * @param fetcher       真正发起请求（及必要的响应数据整形）的函数，返回值类型必须与
 *                      内嵌状态里存放的预载值类型一致。
 * @param paramsBuilder 可选：根据调用参数算出 query 参数对象，用于拼 key。
 */
export function withPreload<TArgs extends unknown[], TResult>(
  method: string,
  urlTemplate: (...args: TArgs) => string,
  fetcher: (...args: TArgs) => Promise<TResult>,
  paramsBuilder?: (...args: TArgs) => Record<string, unknown> | undefined,
): (...args: TArgs) => Promise<TResult> {
  return (...args: TArgs): Promise<TResult> => {
    const url = urlTemplate(...args);
    const params = paramsBuilder ? paramsBuilder(...args) : undefined;
    const key = buildPreloadKey(method, url, params);

    if (!consumedDirectKeys.has(key)) {
      const preloaded = readPreload<TResult>(key);
      if (preloaded !== undefined) {
        consumedDirectKeys.add(key);
        return Promise.resolve(preloaded);
      }
    }

    return fetcher(...args);
  };
}

/** 仅供测试使用：判断某个 direct key 当前是否存在于内嵌状态（不受消费标记影响）。 */
export function __hasEmbeddedDirectKeyForTest(key: string): boolean {
  return !!embeddedState && Object.prototype.hasOwnProperty.call(embeddedState.direct, key);
}
